import { useState, useEffect, useRef, useCallback } from 'react';
import Modal from '../../../components/Modal';

const collapseRepeatedScanPayload = (rawScanValue) => {
    const trimmedScanValue = (rawScanValue || '').trim();
    const payloadLength = trimmedScanValue.length;
    if (!payloadLength || payloadLength % 2 !== 0) {
        return trimmedScanValue;
    }

    const halfPayloadLength = payloadLength / 2;
    const firstHalfPayload = trimmedScanValue.slice(0, halfPayloadLength);
    const secondHalfPayload = trimmedScanValue.slice(halfPayloadLength);
    return firstHalfPayload === secondHalfPayload ? firstHalfPayload : trimmedScanValue;
};

const SCANNER_DEBUG_LOG_LIMIT = 12;

export default function BatchProcessPutaway({
    selectedLines,
    handleCompleteBatch,
    handleSetNotification,
    moveInventoryLocation,
    scannerStep,
    setScannerStep,
    activeLineForLocation,
    setActiveLineForLocation,
    scannerSteps,
    normalizeInventoryLineName,
    normalizeLocationName,
    showScannerDebug = false,
}) {
    const [showExitModal, setShowExitModal] = useState(false);
    const [finalizingBatchAction, setFinalizingBatchAction] = useState('');
    const hiddenScannerInputRef = useRef(null);
    const hiddenInputBufferRef = useRef('');
    const scannerInputReadOnlyReleaseTimeoutRef = useRef(null);
    const lastSubmissionRef = useRef({ payload: '', submittedAtMs: 0 });
    const scannerStepRef = useRef(scannerStep);
    const activeLineForLocationRef = useRef(activeLineForLocation);
    const [lastCapturedScanValue, setLastCapturedScanValue] = useState('');
    const [scanBufferPreviewValue, setScanBufferPreviewValue] = useState('');
    const [lastSubmittedScanPayload, setLastSubmittedScanPayload] = useState('');
    const [lastApiDebugStatus, setLastApiDebugStatus] = useState('No API request yet');
    const [scannerDebugLogEntries, setScannerDebugLogEntries] = useState([]);
    const [hiddenInputFocused, setHiddenInputFocused] = useState(false);
    const [processedLineDataById, setProcessedLineDataById] = useState({});
    const [processingInventoryLineId, setProcessingInventoryLineId] = useState(null);
    const [processingInventoryLineName, setProcessingInventoryLineName] = useState('');
    const [processingDestinationLocationName, setProcessingDestinationLocationName] = useState('');
    const [onScreenError, setOnScreenError] = useState(null);
    const [scannerInputReadOnly, setScannerInputReadOnly] = useState(true);

    const processedLineCount = Object.keys(processedLineDataById).length;
    const allBatchLinesProcessed = selectedLines.length > 0 && processedLineCount === selectedLines.length;

    const appendScannerDebugEntry = useCallback(
        (debugLabel, debugMessage) => {
            if (!showScannerDebug) {
                return;
            }
            const timestampLabel = new Date().toLocaleTimeString();
            const nextDebugEntry = `${timestampLabel} | ${debugLabel}: ${debugMessage}`;
            setScannerDebugLogEntries((previousScannerDebugLogEntries) => {
                const updatedScannerDebugLogEntries = [...previousScannerDebugLogEntries, nextDebugEntry];
                return updatedScannerDebugLogEntries.slice(-SCANNER_DEBUG_LOG_LIMIT);
            });
        },
        [showScannerDebug]
    );

    const focusHiddenScannerInput = useCallback(() => {
        if (showExitModal || Boolean(processingInventoryLineId) || Boolean(finalizingBatchAction)) {
            return;
        }
        const scannerInputElement = hiddenScannerInputRef.current;
        if (!scannerInputElement) {
            return;
        }

        if (scannerInputReadOnlyReleaseTimeoutRef.current) {
            clearTimeout(scannerInputReadOnlyReleaseTimeoutRef.current);
            scannerInputReadOnlyReleaseTimeoutRef.current = null;
        }

        setScannerInputReadOnly(true);

        const focusScannerField = () => {
            try {
                scannerInputElement.focus({ preventScroll: true });
            } catch (focusError) {
                scannerInputElement.focus();
            }
            scannerInputReadOnlyReleaseTimeoutRef.current = window.setTimeout(() => {
                setScannerInputReadOnly(false);
            }, 120);
        };

        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(focusScannerField);
            return;
        }

        focusScannerField();
    }, [finalizingBatchAction, processingInventoryLineId, showExitModal]);

    const clearHiddenInputValue = useCallback(() => {
        hiddenInputBufferRef.current = '';
        if (hiddenScannerInputRef.current) {
            hiddenScannerInputRef.current.value = '';
        }
        setScanBufferPreviewValue('');
    }, []);

    const shouldSkipDuplicateSubmission = useCallback((nextSubmissionPayload) => {
        const dedupeWindowMs = 250;
        const nowMs = Date.now();
        const { payload: previousPayload, submittedAtMs: previousSubmittedAtMs } = lastSubmissionRef.current;
        if (
            previousPayload &&
            previousPayload === nextSubmissionPayload &&
            nowMs - previousSubmittedAtMs <= dedupeWindowMs
        ) {
            return true;
        }

        lastSubmissionRef.current = {
            payload: nextSubmissionPayload,
            submittedAtMs: nowMs,
        };
        return false;
    }, []);

    const showScannerError = (errorTitle, errorDescription, onScreenErrorText) => {
        appendScannerDebugEntry('scannerError', `${errorTitle} | ${errorDescription}`);
        handleSetNotification({
            title: errorTitle,
            desc: errorDescription,
            status: 'error',
        });
        setOnScreenError(onScreenErrorText);
    };

    const resetScanCaptureState = () => {
        clearHiddenInputValue();
    };

    const handleClearCurrentInventoryLineSelection = () => {
        setActiveLineForLocation(null);
        setScannerStep(scannerSteps.awaitInventoryLineSelection);
        setOnScreenError(null);
        handleSetNotification(null);
        resetScanCaptureState();
        appendScannerDebugEntry('stateTransition', scannerSteps.awaitInventoryLineSelection);
    };

    const handleInventoryLineSelection = (rawScannedInventoryLineName) => {
        appendScannerDebugEntry('scanRawInventory', rawScannedInventoryLineName || '(empty)');
        const normalizedInventoryLineName = normalizeInventoryLineName(rawScannedInventoryLineName);
        appendScannerDebugEntry('scanNormalizedInventory', normalizedInventoryLineName || '(empty)');
        if (!normalizedInventoryLineName) {
            appendScannerDebugEntry('scanIgnored', 'normalized inventory line was empty');
            return;
        }

        // Clear previous notification immediately when handling a fresh scan.
        handleSetNotification(null);

        const matchedInventoryLine = selectedLines.find(
            (selectedLine) => normalizeInventoryLineName(selectedLine.Name) === normalizedInventoryLineName
        );

        if (!matchedInventoryLine) {
            showScannerError(
                'Inventory Line Not Found',
                `${normalizedInventoryLineName} is not in this active batch.`,
                `Inventory line not in batch: ${normalizedInventoryLineName}`
            );
            return;
        }

        if (processedLineDataById[matchedInventoryLine.Id]) {
            appendScannerDebugEntry('scanReselectedProcessed', normalizedInventoryLineName);
            return;
        }

        setOnScreenError(null);
        handleSetNotification(null);
        setActiveLineForLocation(matchedInventoryLine);
        setScannerStep(scannerSteps.awaitLocationForSelectedLine);
        appendScannerDebugEntry(
            'stateTransition',
            `${scannerSteps.awaitLocationForSelectedLine} for ${matchedInventoryLine.Name}`
        );
    };

    const handleLocationScanForSelectedLine = async (rawScannedLocationName) => {
        const currentActiveLineForLocation = activeLineForLocationRef.current;
        appendScannerDebugEntry('scanRawLocation', rawScannedLocationName || '(empty)');
        if (!currentActiveLineForLocation) {
            setScannerStep(scannerSteps.awaitInventoryLineSelection);
            appendScannerDebugEntry('stateTransition', scannerSteps.awaitInventoryLineSelection);
            return;
        }

        const normalizedLocationName = normalizeLocationName(rawScannedLocationName);
        appendScannerDebugEntry('scanNormalizedLocation', normalizedLocationName || '(empty)');
        if (!normalizedLocationName) {
            appendScannerDebugEntry('scanIgnored', 'normalized location was empty');
            return;
        }

        // Clear previous notification immediately when handling a fresh scan.
        handleSetNotification(null);

        setProcessingInventoryLineId(currentActiveLineForLocation.Id);
        setProcessingInventoryLineName(currentActiveLineForLocation.Name || '');
        setProcessingDestinationLocationName(normalizedLocationName);
        setOnScreenError(null);
        setLastApiDebugStatus(`Moving ${currentActiveLineForLocation.Name} to ${normalizedLocationName}`);
        appendScannerDebugEntry('fetchStart', '/wms/putaway/move-inventory-location');

        try {
            const moveLocationResult = await moveInventoryLocation(
                normalizedLocationName,
                currentActiveLineForLocation.Id,
                currentActiveLineForLocation.originalLocationId
            );

            if (!moveLocationResult || moveLocationResult?.error) {
                const locationMoveErrorMessage = moveLocationResult?.message || 'Location not found or invalid';
                setLastApiDebugStatus(`Location move failed: ${locationMoveErrorMessage}`);
                appendScannerDebugEntry('fetchError', locationMoveErrorMessage);
                showScannerError(
                    'Location Scan Failed',
                    locationMoveErrorMessage,
                    `Location error: ${locationMoveErrorMessage}`
                );
                return;
            }

            setLastApiDebugStatus(`Location move success for ${currentActiveLineForLocation.Name}`);
            appendScannerDebugEntry('fetchSuccess', `/wms/putaway/move-inventory-location -> ${normalizedLocationName}`);
            handleSetNotification(null);
            setProcessedLineDataById((previousProcessedLineDataById) => ({
                ...previousProcessedLineDataById,
                [currentActiveLineForLocation.Id]: {
                    finalLocationName: normalizedLocationName,
                    inventoryLineName: currentActiveLineForLocation.Name,
                },
            }));
        } catch (locationMoveError) {
            const unexpectedLocationMoveError = locationMoveError?.message || 'Location request failed unexpectedly';
            setLastApiDebugStatus(`Location move failed: ${unexpectedLocationMoveError}`);
            appendScannerDebugEntry('fetchError', unexpectedLocationMoveError);
            showScannerError(
                'Location Scan Failed',
                unexpectedLocationMoveError,
                `Location error: ${unexpectedLocationMoveError}`
            );
        } finally {
            setProcessingInventoryLineId(null);
            setProcessingInventoryLineName('');
            setProcessingDestinationLocationName('');
            setActiveLineForLocation(null);
            setScannerStep(scannerSteps.awaitInventoryLineSelection);
            appendScannerDebugEntry('stateTransition', scannerSteps.awaitInventoryLineSelection);
            resetScanCaptureState();
        }
    };

    const submitScannedPayload = async (providedScannedValue, captureSource = 'globalKeyListener') => {
        const currentScannerStep = scannerStepRef.current;
        const currentActiveLineForLocation = activeLineForLocationRef.current;
        const rawScannedValue = (providedScannedValue || '').trim();
        const scannedValue = collapseRepeatedScanPayload(rawScannedValue);
        resetScanCaptureState();
        setLastCapturedScanValue(scannedValue);
        setLastSubmittedScanPayload(scannedValue || '(empty)');
        appendScannerDebugEntry('submitStep', currentScannerStep);
        appendScannerDebugEntry('submitRaw', rawScannedValue || '(empty)');
        appendScannerDebugEntry('submitCollapsed', scannedValue || '(empty)');
        appendScannerDebugEntry('submitSource', captureSource);

        if (!scannedValue) {
            appendScannerDebugEntry('submitIgnored', 'collapsed payload was empty');
            return;
        }

        if (shouldSkipDuplicateSubmission(scannedValue)) {
            appendScannerDebugEntry('submitDeduped', `${captureSource} duplicate ignored`);
            return;
        }

        // Route to location handling whenever an INV line is already staged.
        // This avoids accidental INV normalization for location barcodes if scannerStep lags.
        if (currentActiveLineForLocation) {
            appendScannerDebugEntry('submitRoute', 'locationForActiveInventoryLine');
            await handleLocationScanForSelectedLine(scannedValue);
            return;
        }

        if (currentScannerStep === scannerSteps.awaitLocationForSelectedLine) {
            appendScannerDebugEntry('submitRoute', 'locationByScannerStep');
            await handleLocationScanForSelectedLine(scannedValue);
            return;
        }

        appendScannerDebugEntry('submitRoute', 'inventoryLineSelection');
        handleInventoryLineSelection(scannedValue);
    };

    const syncHiddenInputBufferValue = useCallback(
        (nextInputValue, debugLabel = 'hiddenInputBuffer') => {
            const inputValue = nextInputValue || '';
            hiddenInputBufferRef.current = inputValue;
            setScanBufferPreviewValue(inputValue);
            setLastCapturedScanValue(inputValue || '');
            appendScannerDebugEntry(debugLabel, `${inputValue || '(empty)'} | len=${inputValue.length}`);
        },
        [appendScannerDebugEntry]
    );

    const resolveDeferredHiddenInputSubmit = useCallback(async () => {
        const submittedValue = (hiddenInputBufferRef.current || hiddenScannerInputRef.current?.value || '').trim();
        appendScannerDebugEntry(
            'hiddenInputSubmitResolved',
            `${submittedValue || '(empty)'} | len=${submittedValue.length}`
        );
        clearHiddenInputValue();
        await submitScannedPayload(submittedValue, 'hiddenInput');
    }, [appendScannerDebugEntry, clearHiddenInputValue]);

    const handleHiddenScannerInputChange = (inputChangeEvent) => {
        const inputValue = inputChangeEvent.target.value || '';
        syncHiddenInputBufferValue(inputValue, 'hiddenInputBuffer');
    };

    const handleHiddenScannerInputKeyDown = async (keyboardEvent) => {
        if (keyboardEvent.key !== 'Enter' && keyboardEvent.key !== 'NumpadEnter') {
            return;
        }

        appendScannerDebugEntry(
            'hiddenInputEnter',
            `${keyboardEvent.key || '(empty)'} | len=${(keyboardEvent.currentTarget?.value || '').length}`
        );
        keyboardEvent.preventDefault();
        appendScannerDebugEntry('hiddenInputSubmitDeferred', keyboardEvent.key);
        await resolveDeferredHiddenInputSubmit();
    };

    const handleManualComplete = async () => {
        if (processedLineCount !== selectedLines.length || selectedLines.length === 0) {
            appendScannerDebugEntry('completeBlocked', 'not all lines are processed');
            return;
        }
        if (finalizingBatchAction) {
            appendScannerDebugEntry('completeBlocked', 'batch finalization is already in progress');
            return;
        }
        appendScannerDebugEntry('completeRequested', 'manual complete batch click');
        setFinalizingBatchAction('Complete');
        try {
            const didCompleteBatch = await handleCompleteBatch('Complete');
            if (!didCompleteBatch) {
                appendScannerDebugEntry('completeFailed', 'complete batch request was not successful');
                setFinalizingBatchAction('');
            }
        } catch (completeBatchError) {
            appendScannerDebugEntry('completeError', completeBatchError?.message || 'complete batch failed');
            setFinalizingBatchAction('');
        }
    };

    const handleExitBatch = async () => {
        if (finalizingBatchAction) {
            appendScannerDebugEntry('exitBlocked', 'batch finalization is already in progress');
            return;
        }
        setShowExitModal(false);
        appendScannerDebugEntry('exitRequested', 'exit batch confirm click');
        setFinalizingBatchAction('Exit');
        try {
            const didExitBatch = await handleCompleteBatch('Exit');
            if (!didExitBatch) {
                appendScannerDebugEntry('exitFailed', 'exit batch request was not successful');
                setFinalizingBatchAction('');
            }
        } catch (exitBatchError) {
            appendScannerDebugEntry('exitError', exitBatchError?.message || 'exit batch failed');
            setFinalizingBatchAction('');
        }
    };

    useEffect(() => {
        scannerStepRef.current = scannerStep;
    }, [scannerStep]);

    useEffect(() => {
        activeLineForLocationRef.current = activeLineForLocation;
    }, [activeLineForLocation]);

    useEffect(() => {
        if (selectedLines.length === 0) {
            return;
        }
        if (processedLineCount !== selectedLines.length) {
            if (scannerStep === scannerSteps.batchReadyToComplete) {
                setScannerStep(scannerSteps.awaitInventoryLineSelection);
                appendScannerDebugEntry('stateTransition', scannerSteps.awaitInventoryLineSelection);
            }
            return;
        }

        if (activeLineForLocation || processingInventoryLineId) {
            return;
        }

        setScannerStep(scannerSteps.batchReadyToComplete);
        appendScannerDebugEntry('stateTransition', scannerSteps.batchReadyToComplete);
    }, [
        activeLineForLocation,
        processedLineCount,
        processingInventoryLineId,
        scannerStep,
        scannerSteps,
        selectedLines.length,
        setScannerStep,
        appendScannerDebugEntry,
    ]);

    useEffect(() => {
        return () => {
            if (scannerInputReadOnlyReleaseTimeoutRef.current) {
                clearTimeout(scannerInputReadOnlyReleaseTimeoutRef.current);
                scannerInputReadOnlyReleaseTimeoutRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        focusHiddenScannerInput();
    }, [focusHiddenScannerInput]);

    return (
        <div className='px-2 w-full sm:px-0'>
            {processingInventoryLineId ? (
                <div className='fixed inset-0 z-[70] flex justify-center items-center bg-black/35'>
                    <div className='flex flex-col gap-2 justify-center items-center p-4 rounded-md border border-blue-200 bg-white/95 min-w-[220px]'>
                        <div className='w-12 h-12 rounded-full border-4 border-blue-200 animate-spin border-t-blue-primary'></div>
                        <p className='text-sm font-semibold text-blue-900'>Processing Location Move...</p>
                        <p className='text-sm font-semibold text-blue-900'>{processingInventoryLineName || '-'}</p>
                        <p className='text-sm font-semibold text-blue-900'>{processingDestinationLocationName || '-'}</p>
                    </div>
                </div>
            ) : null}
            {finalizingBatchAction ? (
                <div className='fixed inset-0 z-[70] flex justify-center items-center bg-black/35'>
                    <div className='flex flex-col gap-2 justify-center items-center p-4 rounded-md border border-blue-200 bg-white/95 min-w-[220px]'>
                        <div className='w-12 h-12 rounded-full border-4 border-blue-200 animate-spin border-t-blue-primary'></div>
                        <p className='text-sm font-semibold text-blue-900'>Processing Batch {finalizingBatchAction}...</p>
                        <p className='text-sm font-semibold text-blue-900'>Returning to line scan screen...</p>
                    </div>
                </div>
            ) : null}
            <input
                ref={hiddenScannerInputRef}
                type='text'
                autoComplete='off'
                autoCorrect='off'
                autoCapitalize='off'
                spellCheck={false}
                inputMode='text'
                showSoftInputOnFocus={false}
                readOnly={scannerInputReadOnly}
                className='fixed right-2 bottom-2 z-50 p-1 w-24 h-8 text-[10px] text-green-700 bg-green-100 rounded border border-green-500'
                style={{
                    position: 'fixed',
                    right: '8px',
                    bottom: '8px',
                    opacity: 0.85,
                }}
                placeholder='Scan field'
                onFocus={() => {
                    setHiddenInputFocused(true);
                    appendScannerDebugEntry('hiddenInputFocus', 'focused');
                }}
                onBlur={() => {
                    setHiddenInputFocused(false);
                    appendScannerDebugEntry('hiddenInputFocus', 'blurred');
                }}
                onChange={handleHiddenScannerInputChange}
                onKeyDown={handleHiddenScannerInputKeyDown}
            />
            <div className='flex flex-col gap-2 justify-between items-start mb-4 select-none sm:flex-row sm:gap-x-4 sm:items-center'>
                <div className='w-full sm:w-auto'>
                    <div className='p-2 w-full text-xs text-gray-700 rounded-md border border-gray-300 sm:w-64 sm:text-sm'>
                        <p className='font-semibold'>Scanner Status</p>
                        <p className='mt-1 break-all text-[11px] sm:text-xs'>
                            Last scan: {lastCapturedScanValue || 'Waiting for scan...'}
                        </p>
                    </div>
                    {showScannerDebug ? (
                        <div className='p-2 mt-2 w-full text-xs text-green-700 rounded-md border border-green-500 bg-green-50 sm:w-[28rem] sm:text-sm'>
                            <p className='font-semibold text-green-700'>Scanner Debug</p>
                            <p>Current Step: {scannerStep}</p>
                            <p>
                                Capture Paused:{' '}
                                {showExitModal || Boolean(processingInventoryLineId) || Boolean(finalizingBatchAction)
                                    ? 'true'
                                    : 'false'}
                            </p>
                            <p>Hidden Input Focused: {hiddenInputFocused ? 'true' : 'false'}</p>
                            <p className='break-all'>Buffer Preview: {scanBufferPreviewValue || '(empty)'}</p>
                            <p className='break-all'>Last Submitted: {lastSubmittedScanPayload || '(none)'}</p>
                            <p className='break-all'>Last API Status: {lastApiDebugStatus}</p>
                            <div className='pt-1 mt-1 border-t border-green-300'>
                                {scannerDebugLogEntries.length === 0 ? (
                                    <p>No scanner events yet.</p>
                                ) : (
                                    scannerDebugLogEntries.map((scannerDebugLogEntry) => (
                                        <p key={scannerDebugLogEntry} className='leading-tight break-all'>
                                            {scannerDebugLogEntry}
                                        </p>
                                    ))
                                )}
                            </div>
                        </div>
                    ) : null}
                    <div className='flex gap-2 justify-between items-center mt-1'>
                        <p className='text-xs text-gray-600 sm:text-sm'>
                            {scannerStep === scannerSteps.awaitLocationForSelectedLine && activeLineForLocation
                                ? `Current Step: Scan destination for ${activeLineForLocation.Name}`
                                : scannerStep === scannerSteps.batchReadyToComplete
                                ? 'Current Step: Completing batch (or rescan INV to update location)'
                                : 'Current Step: Scan an inventory line'}
                        </p>
                        {activeLineForLocation ? (
                            <button
                                type='button'
                                className='text-xs text-blue-700 underline hover:text-blue-500 sm:text-sm disabled:opacity-50 disabled:no-underline'
                                onClick={handleClearCurrentInventoryLineSelection}
                                disabled={Boolean(processingInventoryLineId)}
                            >
                                Clear Current INV
                            </button>
                        ) : null}
                    </div>
                </div>

                {onScreenError ? <p className='w-full text-xs text-red-500 sm:w-64 sm:text-sm'>{onScreenError}</p> : null}

                <div className='flex gap-2 w-full sm:gap-x-4 sm:w-auto'>
                    <button
                        className='flex-1 p-2 text-sm text-white bg-red-500 rounded-md hover:bg-red-400 sm:text-base sm:flex-none disabled:opacity-50'
                        onClick={() => setShowExitModal(true)}
                        disabled={allBatchLinesProcessed || Boolean(finalizingBatchAction)}
                    >
                        Exit
                    </button>
                    <button
                        className='flex-1 p-2 text-sm text-white rounded-md cursor-pointer bg-blue-primary hover:bg-blue-primary/90 sm:text-base sm:flex-none disabled:opacity-50'
                        disabled={!allBatchLinesProcessed || Boolean(finalizingBatchAction)}
                        onClick={handleManualComplete}
                    >
                        Complete Batch
                    </button>
                </div>
            </div>
            <div className='w-full'>
                <table className='w-full text-xs table-fixed select-text sm:text-sm'>
                    <thead className='text-white bg-blue-primary'>
                        <tr className='flex'>
                            <th className='pl-2 sm:pl-4 pr-2 flex-[1.3_1_0%] min-w-0 py-2 text-left'>Name</th>
                            <th className='pl-2 sm:pl-4 pr-2 flex-[1.1_1_0%] min-w-0 py-2 text-left'>Previous Location</th>
                            <th className='px-2 sm:px-4 pr-2 flex-[1.1_1_0%] min-w-0 py-2 text-left'>New Location</th>
                            <th className='pl-2 sm:pl-4 pr-2 flex-[0.9_1_0%] min-w-0 py-2 text-left'>
                                Processed {processedLineCount}/{selectedLines.length}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {selectedLines.map((selectedLine) => {
                            const processedLineData = processedLineDataById[selectedLine.Id];
                            const isSelectedForLocationScan =
                                activeLineForLocation && activeLineForLocation.Id === selectedLine.Id;
                            const isRowBeingProcessed = processingInventoryLineId === selectedLine.Id;

                            return (
                                <tr
                                    key={selectedLine.Id}
                                    className='flex items-center border select-text even:bg-gray-200 min-h-[40px] sm:h-10'
                                >
                                    <td className='pl-2 sm:pl-4 pr-2 flex-[1.3_1_0%] min-w-0 py-1 sm:py-0'>
                                        <a
                                            className='block text-xs text-blue-900 underline truncate cursor-pointer hover:text-blue-400 sm:text-sm'
                                            href={selectedLine.invLineLink}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            title={selectedLine?.Name || ''}
                                        >
                                            {selectedLine?.Name || ''}
                                        </a>
                                    </td>
                                    <td
                                        className='pl-2 sm:pl-4 pr-2 flex-[1.1_1_0%] min-w-0 py-1 sm:py-0 text-xs truncate sm:text-sm'
                                        title={selectedLine?.originalLocationName || ''}
                                    >
                                        {selectedLine.originalLocationName}
                                    </td>
                                    <td className='px-2 sm:px-4 pr-2 flex-[1.1_1_0%] min-w-0 py-1 sm:py-0'>
                                        <span
                                            className='block text-xs truncate sm:text-sm'
                                            title={
                                                isSelectedForLocationScan
                                                    ? 'Awaiting scan...'
                                                    : processedLineData?.finalLocationName || ''
                                            }
                                        >
                                            {isSelectedForLocationScan
                                                ? 'Awaiting scan...'
                                                : processedLineData?.finalLocationName || ''}
                                        </span>
                                    </td>
                                    <td className='pl-2 sm:pl-4 pr-2 flex-[0.9_1_0%] min-w-0 py-1 sm:py-0'>
                                        {isRowBeingProcessed ? (
                                            <span className='text-xs text-blue-700 sm:text-sm'>Processing...</span>
                                        ) : isSelectedForLocationScan ? (
                                            <span className='text-xs text-amber-700 sm:text-sm'>Scan location</span>
                                        ) : processedLineData ? (
                                            <span className='text-xs text-green-700 sm:text-sm'>Processed</span>
                                        ) : (
                                            <span className='text-xs text-gray-500 sm:text-sm'>Pending</span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <Modal
                open={showExitModal}
                setOpen={setShowExitModal}
                title='Exit Batch Process'
                body={`Are you sure you want to exit the batch process?`}
                onConfirm={handleExitBatch}
            />
        </div>
    );
}
